import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { appendScreenshotLang } from "../../lib/screenshotI18n";
import {
    DEFAULT_SCREENSHOT_DEVICE,
    SCREENSHOT_DEVICES,
    resolveScreenshotDevice,
} from "../../components/ScreenshotDevicePicker";

function screenshotPageHref(path, lang, device) {
    if (typeof window === "undefined") return path;
    return `${window.location.origin}${appendScreenshotLang(path, lang, device)}`;
}

function ScreenshotPreviewTile({
    href,
    label,
    slug,
    onOpen,
    tileWidth,
    viewportWidth,
    viewportHeight,
}) {
    const tileHeight = Math.round((tileWidth * viewportHeight) / viewportWidth);
    const iframeScale = tileWidth / viewportWidth;
    return (
        <Box
            sx={{
                flex: `0 0 ${tileWidth}px`,
                width: tileWidth,
            }}
        >
            <Box
                role="button"
                tabIndex={0}
                onClick={onOpen}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        onOpen(e);
                    }
                }}
                aria-label={`Open screenshot ${label}`}
                sx={{
                    position: "relative",
                    width: tileWidth,
                    height: tileHeight,
                    overflow: "hidden",
                    borderRadius: 1.5,
                    border: 1,
                    borderColor: "divider",
                    bgcolor: "#fff",
                    boxShadow: 1,
                    cursor: "pointer",
                    transition: "box-shadow 0.15s ease, transform 0.15s ease",
                    "&:hover": {
                        boxShadow: 3,
                        transform: "translateY(-2px)",
                    },
                }}
            >
                <Box
                    component="iframe"
                    src={href}
                    title={label}
                    loading="lazy"
                    sx={{
                        width: viewportWidth,
                        height: viewportHeight,
                        border: 0,
                        transform: `scale(${iframeScale})`,
                        transformOrigin: "top left",
                        pointerEvents: "none",
                        display: "block",
                    }}
                />
            </Box>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: "block",
                    mt: 0.75,
                    textAlign: "center",
                    lineHeight: 1.25,
                    fontWeight: 600,
                }}
            >
                {slug}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: "block",
                    textAlign: "center",
                    lineHeight: 1.25,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

/**
 * Wrapping gallery of live screenshot previews at the selected device size.
 */
export default function ScreenshotPreviewRow({
    scenes,
    screenshotPath,
    lang,
    onOpenScene,
    tileWidth = 136,
    device = DEFAULT_SCREENSHOT_DEVICE,
}) {
    if (!scenes?.length) return null;
    const deviceKey = resolveScreenshotDevice(device);
    const { width: viewportWidth, height: viewportHeight } =
        SCREENSHOT_DEVICES[deviceKey];

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 1.5,
                alignItems: "flex-start",
                pb: 1.5,
                pt: 0.5,
                mx: -0.5,
                px: 0.5,
            }}
        >
            {scenes.map((scene) => {
                const path = screenshotPath(scene.slug);
                const href = screenshotPageHref(path, lang, deviceKey);
                const label = scene.label ?? scene.slug;
                return (
                    <ScreenshotPreviewTile
                        key={scene.slug}
                        href={href}
                        slug={scene.slug}
                        label={label}
                        onOpen={onOpenScene(path, scene.slug)}
                        tileWidth={tileWidth}
                        viewportWidth={viewportWidth}
                        viewportHeight={viewportHeight}
                    />
                );
            })}
        </Box>
    );
}
